/**
 * Combo — the searchable dropdown, ported 1:1 from the monolith's enhanceSelect()
 * (legacy/smart-email-request/index.html L6336–6360). The monolith wraps a native
 * <select>; here the same contract is a controlled component:
 *
 *   - button face (.combo-btn) + floating panel (.combo-panel)
 *   - a search box appears only when there are MORE THAN 12 options (big()),
 *     focused ~20ms after open, case-insensitive substring filter
 *   - optgroup labels render as gold uppercase headers (#a9843f, 9.5px/800/.07em)
 *   - selection on mousedown (so the outside-click closer never races it)
 *   - .ph placeholder styling when empty and the placeholder text matches /select|add/i
 *   - "No matches" empty row
 *   - `clearable`: enhanceSelect renders EVERY option of the underlying select,
 *     including a blank "— select —" one when the select has it — so a value can
 *     be cleared by re-picking the blank row (audit 2026-08-08 #11)
 *
 * Deliberately NOT added (the monolith doesn't have them — parity over polish):
 * keyboard navigation, Enter-picks-first, free-text entry, an always-on search.
 * If we ever add those, add them to the monolith first or the two tools drift.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface ComboOpt { v: string; label?: string }
export interface ComboGroup { label?: string; opts: ComboOpt[] }

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** flat list or grouped; groups render the gold headers */
  options: ComboOpt[] | ComboGroup[];
  placeholder?: string;
  className?: string;
  /** chipbox mode: borderless face like `.chipbox .combo-btn` in the monolith */
  chip?: boolean;
  disabled?: boolean;
  title?: string;
  /** render the blank placeholder row so the value can be cleared back to ""
   *  (only for fields whose monolith <select> carries a blank option) */
  clearable?: boolean;
  /**
   * ── A LIST THAT IS STILL LOADING SAYS SO (2026-09-11) ────────────────────────
   * Uche on the 2026-09-10 testing call: "some sort of loading indicator while the
   * preview or a dropdown is fetching." The division pickers show a bundled list at
   * once and overlay the live /refdata answer a beat later — so a requester who opened
   * Community early saw a list that was quietly wrong, with nothing saying more was on
   * its way. `loading` puts the spinner in the face (in place of the caret) and a
   * loading row at the top of the panel; the options that ARE there stay pickable.
   * With no options at all the face reads `loadingText` instead of the placeholder.
   * Mock approved by Jack: SPINNER-MOCK-20260911.html (panel B).
   */
  loading?: boolean;
  /** what is loading — "Loading communities…" — for the face and the panel row */
  loadingText?: string;
}

function isGrouped(o: ComboOpt[] | ComboGroup[]): o is ComboGroup[] {
  return o.length > 0 && (o as ComboGroup[])[0].opts !== undefined;
}

export default function Combo({ value, onChange, options, placeholder = "— select —", className = "", chip = false, disabled = false, title, clearable = false, loading = false, loadingText = "Loading…" }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  /* ── THE PANEL IS PORTALLED, BECAUSE THE FORM SCROLLS (2026-08-22) ─────────────
     Jack, on the Area of Interest list: "the area of interest is cut off." The
     panel was `position:absolute` inside the field, and `.fc-body` — the card's own
     scroll container — CLIPS an absolutely positioned child. Any combo near the
     bottom of the card lost the end of its list, which on Area of Interest is most
     of it. Fixed-positioned in a portal on <body>, measured off the button, it is
     clipped by nothing; it flips above the field when there is more room up there,
     and its height is capped to the space that actually exists. */
  const panelRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; width: number; top?: number; bottom?: number; maxH: number } | null>(null);
  const place = useCallback(() => {
    const btn = rootRef.current?.querySelector(".combo-btn") as HTMLElement | null;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vh = window.innerHeight || 0;
    const below = vh - r.bottom - 12;
    const above = r.top - 12;
    const up = below < 220 && above > below;   /* only flip when it genuinely helps */
    setBox({
      left: Math.round(r.left), width: Math.round(r.width),
      ...(up ? { bottom: Math.round(vh - r.top + 5) } : { top: Math.round(r.bottom + 5) }),
      maxH: Math.max(140, Math.min(340, Math.round(up ? above : below))),
    });
  }, []);

  const groups: ComboGroup[] = isGrouped(options) ? options : [{ opts: options }];
  /* the monolith's big() counts sel.options.length — which INCLUDES the blank /
     "+ Add …" placeholder option the clearable combos carry (index.html L6346) */
  const total = groups.reduce((n, g) => n + g.opts.length, 0) + (clearable ? 1 : 0);
  const big = total > 12; /* the monolith's search threshold */

  const sel = groups.flatMap((g) => g.opts).find((o) => o.v === value);
  /* an empty list that is still loading reads as loading, not as "— select —" */
  const emptyLoading = loading && total === 0 && !sel;
  const face = sel ? (sel.label || sel.v) : emptyLoading ? loadingText : placeholder;
  const ph = !value && (emptyLoading || /select|add/i.test(placeholder));

  useEffect(() => {
    if (!open) { setBox(null); return; }
    place();
    /* the form scrolls under the panel, so it has to be re-measured while open */
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      const inRoot = !!rootRef.current && rootRef.current.contains(t);
      /* the panel lives on <body> now — a click inside it is NOT an outside click */
      const inPanel = !!panelRef.current && panelRef.current.contains(t);
      if (!inRoot && !inPanel) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    /* the monolith focuses the search 20ms after open */
    const t = window.setTimeout(() => { if (big) searchRef.current?.focus(); }, 20);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
      window.clearTimeout(t);
    };
  }, [open, big, place]);

  const ql = q.trim().toLowerCase();
  const shown = groups
    .map((g) => ({ ...g, opts: ql ? g.opts.filter((o) => (o.label || o.v).toLowerCase().includes(ql)) : g.opts }))
    .filter((g) => g.opts.length);

  const pick = (v: string) => { onChange(v); setOpen(false); setQ(""); };
  /* the placeholder row is subject to the same substring filter as real options */
  const phRow = clearable && (!ql || placeholder.toLowerCase().includes(ql));

  return (
    <div className={"combo" + (className ? " " + className : "")} ref={rootRef}>
      <button type="button" className={"combo-btn" + (ph ? " ph" : "")} disabled={disabled} title={title}
        onClick={() => { setOpen(!open); setQ(""); }}>
        <span className="combo-lbl">{face}</span>
        {loading
          ? <span className="combo-spin" role="status" aria-label={loadingText} data-testid="combo-loading" />
          : <span className="combo-car">▾</span>}
      </button>
      {open && createPortal((
        <div className="combo-panel combo-fixed" ref={panelRef}
          style={box ? {
            left: box.left, width: box.width, maxHeight: box.maxH,
            ...(box.top !== undefined ? { top: box.top } : {}),
            ...(box.bottom !== undefined ? { bottom: box.bottom } : {}),
          } : { visibility: "hidden" }}>
          {/* the monolith's enhanceSelect ALWAYS appends the search input to the
              panel and only toggles its display by big() on open() (index.html
              L6341/L6352) — the element is present in the DOM for small lists too */}
          <input ref={searchRef} className="combo-search" placeholder="Search…" value={q}
            style={{ display: big ? "block" : "none" }} /* monolith open(): block / none */
            onChange={(e) => setQ(e.target.value)} />
          <div className="combo-opts">
            {loading && (
              <div className="combo-loading" data-testid="combo-loading-row">
                <span className="combo-spin" /><span>{loadingText}</span>
              </div>
            )}
            {shown.length === 0 && !phRow && !loading && <div className="combo-empty">No matches</div>}
            {/* the blank/placeholder option filters like any other row (monolith
                renderOpts runs the substring test on EVERY option's text) */}
            {phRow && (
              <div className={"combo-opt" + (!value ? " on" : "")}
                onMouseDown={(e) => { e.preventDefault(); pick(""); }}>
                {placeholder}
              </div>
            )}
            {/* Fragments, not wrapper divs — headers and rows must be direct flex
                children so every row gets the 1px gap (monolith renderOpts emits
                them as flat siblings; a wrapper div swallowed 1px per row) */}
            {shown.map((g, gi) => (
              <Fragment key={g.label || gi}>
                {g.label && <div className="combo-gh">{g.label}</div>}
                {g.opts.map((o) => (
                  <div key={o.v} className={"combo-opt" + (o.v === value ? " on" : "")}
                    onMouseDown={(e) => { e.preventDefault(); pick(o.v); }}>
                    {o.label || o.v}
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
